// ── Per-platform market lines for the pick-trend chart ────────────
//
// The chart used to resolve ONE market per player — Underdog's dated history
// unless `dominantProvider` said DraftKings, in which case the whole series was
// swapped for DK's coarse trajectory. Every dot then got measured against
// whichever board won the majority vote, so a player drafted 2× on DK and 1× on
// UD had his Underdog pick scored against the DraftKings board. With the split
// columns showing −10 on Underdog and +10 on DraftKings, the chart's summary
// strip could contradict the numbers printed directly above it.
//
// A player now carries one series per platform he was actually drafted on, and
// each pick is scored against its own. The majority vote is gone.

import type {
  AdpDataPoint,
  CsvRow,
  DkAdpTrendPoint,
  Player,
  PlayerTrend,
} from "../types/draft";
import { canonicalName } from "./playerIdentity";
import { sortPlatforms } from "./platformSplit";

/** One platform's market for one player: its dated line plus today's price. */
export interface MarketSeries {
  provider: string;
  /** Dated ADP points, ascending. Empty when the platform has no history. */
  points: AdpDataPoint[];
  /** Current market ADP — the flat fallback when `points` has fewer than 2. */
  current: number | null;
}

/** Provider label for a row, matching DraftDataContext's `rowProvider`. */
function providerOf(row: CsvRow): string {
  return (row["Provider"] || "").trim() || "Unknown";
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Platforms this player was actually drafted on, in display order. */
function providersFor(rows: CsvRow[]): string[] {
  const seen = new Set<string>();
  for (const row of rows) seen.add(providerOf(row));
  return sortPlatforms([...seen]);
}

/** Earliest "Picked At" date (ISO day) among rows on `provider`. */
function firstPickDate(rows: CsvRow[], provider: string): string | null {
  let earliest: string | null = null;
  for (const row of rows) {
    if (providerOf(row) !== provider) continue;
    const day = (row["Picked At"] || "").slice(0, 10);
    if (day && (earliest === null || day < earliest)) earliest = day;
  }
  return earliest;
}

/**
 * Index Underdog's dated history by `canonicalName|position`, keeping a
 * name-only fallback for the ambiguity this file has to survive: the portfolio
 * reconciles a player to ONE position (freshest pick wins), and that can
 * disagree with the position the history file carries — Eli Heidenreich is RB
 * on Underdog and WR on DraftKings. A `name|position` miss would silently drop
 * the market line, so a name-only lookup backs it up. The fallback is only
 * offered when exactly one history entry claims that name; two entries sharing
 * a canonical name are genuinely ambiguous and better left unmatched than
 * guessed.
 */
function indexUdHistory(history: PlayerTrend[]) {
  const byNamePos = new Map<string, AdpDataPoint[]>();
  const byName = new Map<string, AdpDataPoint[] | null>();
  for (const trend of history) {
    const name = canonicalName(trend.name);
    if (!name) continue;
    byNamePos.set(`${name}|${trend.position}`, trend.history);
    byName.set(name, byName.has(name) ? null : trend.history);
  }
  return (name: string, position: string): AdpDataPoint[] =>
    byNamePos.get(`${name}|${position}`) ?? byName.get(name) ?? [];
}

/**
 * DraftKings has no dated history feed, only three anchors (season open, a week
 * ago, now). They become a coarse 3-point line, with the open anchored at the
 * player's first pick on DraftKings so the line renders inside the chart's
 * window. The open point is DROPPED when that anchor is not actually older than
 * the week anchor — a player first drafted in the last seven days would
 * otherwise plot his season-open price to the RIGHT of his week-ago price.
 */
function dkSeries(
  trend: DkAdpTrendPoint,
  openAt: string | null,
  weekStr: string,
  todayStr: string,
): AdpDataPoint[] {
  const points: AdpDataPoint[] = [];
  if (trend.open !== null && openAt && openAt < weekStr) {
    points.push({ date: openAt, adp: trend.open });
  }
  if (trend.week !== null && weekStr < todayStr) {
    points.push({ date: weekStr, adp: trend.week });
  }
  points.push({ date: todayStr, adp: trend.curr });
  return points;
}

/**
 * Market lines for every player, keyed by `canonicalName|position` using the
 * PLAYER's own reconciled position — so the key the page looks up is the key
 * this map wrote, and a position disagreement can only ever be absorbed inside
 * `indexUdHistory`, never turn into a lookup miss at the call site.
 */
export function buildMarketSeriesByKey(
  players: Player[],
  udHistory: PlayerTrend[],
  dkTrend: Record<string, DkAdpTrendPoint>,
  lookupForProvider: (provider?: string) => Map<string, number>,
  now: Date = new Date(),
): Map<string, MarketSeries[]> {
  const udHistoryFor = indexUdHistory(udHistory);
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const todayStr = iso(startOfToday);
  const weekStr = iso(new Date(startOfToday.getTime() - 7 * 86400000));

  const out = new Map<string, MarketSeries[]>();
  for (const player of players) {
    const name = canonicalName(player.name || "");
    if (!name) continue;
    const series: MarketSeries[] = [];
    for (const provider of providersFor(player.rows)) {
      const raw = lookupForProvider(provider).get(name);
      const current = typeof raw === "number" && !isNaN(raw) ? raw : null;
      let points: AdpDataPoint[] = [];
      if (provider === "Underdog") {
        points = udHistoryFor(name, player.position);
      } else if (provider === "DraftKings") {
        const trend = dkTrend[name];
        if (trend) {
          points = dkSeries(trend, firstPickDate(player.rows, provider), weekStr, todayStr);
        }
      }
      // A platform with neither a line nor a price contributes nothing but a
      // legend entry, so drop it rather than draw an empty series.
      if (points.length === 0 && current === null) continue;
      series.push({ provider, points, current });
    }
    if (series.length) out.set(`${name}|${player.position}`, series);
  }
  return out;
}
